import { URLS } from "@/api/urls";
import { Language } from "@/constants";
import { Entity, UserDefinedEntity } from "@/types/Entity";
import { Episode } from "@/types/Episode";
import {
  CandidateSearch,
  Search,
  SearchUserEditableFields,
  UnsavedSearch,
} from "@/types/Search";
import { SearchQuery } from "@/types/SearchQuery";
import { stringifyUrl } from "@/utils";

import fetcher_v2, { ErrorMessages } from "../fetcher_v2";

export type SearchResponse = {
  results?: Episode[];
  meta: {
    page_token: number | null;
    total_matches: number;
  };
};

export type SaveStaticSearchResponse = {
  uuid?: Uuid;
  start_date?: string;
  end_date?: string;
};

export type PreviewSearchErrors = {
  queries?: {
    keywords?: string[];
    near_keywords?: string[];
    near_near_keywords?: string[];
    not_near_keywords?: string[];
    sources?: string[];
    exclude_channel_names?: string[];
    include_channel_names?: string[];
  }[];
  non_field_errors?: string[];
};

type PreviewSettings = {
  preview?: boolean;
  page_size: number;
  page_token?: number;
};

export const previewSearch = (
  search: CandidateSearch | Search,
  settings: PreviewSettings
) => {
  return fetcher_v2<
    SearchResponse,
    PreviewSettings & CandidateSearch,
    ErrorMessages<CandidateSearch>
  >(URLS.SEARCH, {
    method: "POST",
    body: { ...settings, ...search },
  });
};

export const saveStaticSearch = (search: UnsavedSearch) => {
  return fetcher_v2<
    SaveStaticSearchResponse,
    UnsavedSearch & { is_static: true }
  >(URLS.SAVE_STATIC_SEARCH, {
    method: "POST",
    body: {
      ...search,
      is_static: true,
    },
  });
};

type SearchSuggestionParameters = {
  entities: Array<UserDefinedEntity | Entity>;
  languages: Language[];
};

export type SearchSuggestionQuery = Pick<
  SearchQuery,
  "keywords" | "near_keywords"
>;
export type SearchSuggestion = Partial<SearchUserEditableFields> &
  Pick<SearchUserEditableFields, "title" | "search_type"> & {
    queries: SearchSuggestionQuery[];
  };

export const SEARCH_SUGGESTION_INCLUDE = {
  PHONETIC_VARIATIONS: "phonetic_variations",
  NEAR_KEYWORDS: "near_keywords",
  SYNONYM_SUGGESTIONS: "synonym_suggestions",
};
export type SearchSuggestionInclude =
  (typeof SEARCH_SUGGESTION_INCLUDE)[keyof typeof SEARCH_SUGGESTION_INCLUDE];

export const getSearchSuggestion = (
  body: SearchSuggestionParameters,
  includes?: SearchSuggestionInclude[]
) => {
  const url = stringifyUrl({
    url: URLS.SEARCH_SUGGESTIONS,
    query: {
      includes: includes,
    },
  });
  return fetcher_v2<{ results: SearchSuggestion }, SearchSuggestionParameters>(
    url,
    {
      method: "POST",
      body: { ...body },
    }
  );
};
